import { buyersConciergeEvents } from '@consumer/api'
import type ConciergeEventType from '@consumer/enums/BuyersConciergeEventType'
import { trackConversion } from '@shared/tracking'
import type { ConciergeInputVariantName } from './ConciergeInputVariant.vue'
import { SIGNAL_NAMES, type ConciergeSignals, type SignalName, type SignalSource } from './conciergeState'

export type { ConciergeEventType }

export interface ConciergeEventContext {
  variant: ConciergeInputVariantName
  signalKind?: SignalName
  signalOrigin?: SignalSource
  metadata?: Record<string, unknown>
}

// Deliberately posts via the generated client, not sendBeacon (matches the other beacons);
// a result_click fired mid-navigation is best-effort under this transport.
export function trackConciergeEvent (eventType: ConciergeEventType, context: ConciergeEventContext) {
  const { variant, signalKind, signalOrigin, metadata } = context

  // `?.catch`, not `.catch`: the generated client is stubbed in Storybook/tests
  // and resolves to undefined there, so guard the fire-and-forget rejection
  // handler against a non-Promise return.
  buyersConciergeEvents
    .create({
      data: {
        event_type: eventType,
        input_variant: variant,
        signal_kind: signalKind,
        signal_origin: signalOrigin,
        metadata,
      },
    })
    ?.catch(() => {
      // Swallow — funnel tracking is non-critical and must not surface to the buyer.
    })

  trackConversion('buyers_concierge_event', {
    event_type: eventType,
    input_variant: variant,
    signal_kind: signalKind ?? null,
    signal_origin: signalOrigin ?? null,
  })
}

const MAX_QUERY_CHARS = 500
const MAX_INPUT_CHARS = 100

// Raw input behind a search (for results_ready/empty). Madlibs reads `display`, not `value`, so
// entries that didn't resolve are still captured. Taps writes `display` only where the tapped
// label differs from its canonical (a custom entry, "Coworker" → colleague), so it also reads the
// value behind a bare tap — otherwise Mom, Coffee and Birthday would drop out of the event.
// Stored raw by design — gift context, ruled not PII.
export function conciergeQueryMetadata (
  variant: ConciergeInputVariantName,
  raw: string,
  signals: ConciergeSignals,
): Record<string, unknown> {
  if (variant === 'free_text') {
    const query = raw.trim().slice(0, MAX_QUERY_CHARS)
    return query ? { query } : {}
  }

  const inputs: Record<string, string> = {}
  for (const name of SIGNAL_NAMES) {
    const shown = variant === 'taps' ? tappedText(signals[name]) : signals[name].display
    if (shown) inputs[name] = shown.slice(0, MAX_INPUT_CHARS)
  }
  return Object.keys(inputs).length ? { inputs } : {}
}

// Budget has no words of its own (and taps never asks for one), so a range yields nothing.
function tappedText ({ value, display }: ConciergeSignals[SignalName]): string | null {
  if (display) return display
  if (Array.isArray(value)) return value.join(', ') || null
  return typeof value === 'string' ? value : null
}
